import { ChangeEvent, useDeferredValue, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Settings } from '@mint-lib/icons';
import { SearchBar } from '@mint-lib/tw-components/patterns';

import { GlossaryItem } from '../../api/types.js';
import { useChannelsCatalog } from '../../queries/index.js';
import { EmptyState } from '../atoms/EmptyState.jsx';
import type { ChannelGlossaryOptionV2 } from '../types.js';
import { GlossarySelectionListItem } from './GlossarySelectionListItem.jsx';

type ChannelsGlossaryListV2Props = {
  glossaries: GlossaryItem[];
  selectedItems: string[];
  onItemSelect: (itemName: string) => () => void;
};

export const ChannelsGlossaryListV2 = ({
  glossaries,
  selectedItems,
  onItemSelect,
}: ChannelsGlossaryListV2Props) => {
  const { t } = useTranslation('@myn/import-hub');

  const [searchValue, setSearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };
  const handleSearchClear = () => {
    setSearchValue('');
  };

  const {
    data: { channelsByName },
  } = useChannelsCatalog();

  const filteredChannelsList = Object.keys(channelsByName).reduce(
    (acc, channelName) => {
      if (
        channelsByName[channelName].label
          .trim()
          .toLowerCase()
          .includes(deferredSearchValue.trim().toLowerCase())
      ) {
        acc.push({
          name: channelName,
          label: channelsByName[channelName].label,
          logo: <Settings className="fill-icon-primary" />,
          type: null,
          readOnly: glossaries.some(
            (glossary) => glossary.name === channelName,
          ),
        });
      }
      return acc;
    },
    [] as ChannelGlossaryOptionV2[],
  );

  return (
    <>
      <SearchBar
        value={searchValue}
        onChange={handleSearchChange}
        onClear={handleSearchClear}
        placeholder={t('Search by name')}
        size="small"
        className="mb-2 min-h-8 w-full"
      />
      <div className="flex h-full flex-col gap-6">
        <div className="flex flex-col gap-2">
          {filteredChannelsList.map(({ name, label, logo, readOnly }) => (
            <GlossarySelectionListItem
              key={name}
              glossaryType="channel"
              label={label}
              icon={logo}
              readOnly={readOnly}
              selected={selectedItems.includes(name)}
              onSelect={onItemSelect(name)}
            />
          ))}
        </div>
        {deferredSearchValue.length && !filteredChannelsList.length ? (
          <div className="flex h-full flex-col justify-center">
            <EmptyState
              icon="IllustrationNoResults"
              text={t('No results found for "{{searchValue}}"', {
                searchValue: deferredSearchValue,
              })}
              description={t('Please, try searching for something different.')}
              className="border-none shadow-none"
            />
          </div>
        ) : null}
      </div>
    </>
  );
};
